import { Button } from "@/components/ui/button";
import type { NoticeReadMark } from "./notices-box";
import type { Box, ListBox, ListFacet, Read, RowTask } from "@/components/ui/box.schema";
import type { CourseConversation } from "@/lib/api/inbox";
import { inboxWords as words, messageTime } from "./inbox-copy";
import { ConversationThread } from "./conversation-thread";

export type ConversationList = { conversations: CourseConversation[] };

interface ConversationsOptions {
  read: Read<ConversationList>;
  actor: string;
  query: string;
  onQueryChange(query: string): void;
  course: ListFacet;
  channel: ListFacet;
  selected?: string;
  draft: boolean;
  mark?(id:string):NoticeReadMark;
  task(conversation: CourseConversation): RowTask | undefined;
}

function otherPeople(row: CourseConversation, actor: string): string {
  return row.participants.filter(person => person.user_id !== actor).map(person => person.name).join(", ");
}

/** M02/M03: one read and count, with each reply's work in its own row (K3). */
export function conversationsBox(options: ConversationsOptions): Box {
  const { actor, course, channel } = options;
  const needle = options.query.trim().toLocaleLowerCase();
  const shown = (row: CourseConversation) =>
    (!course.value || row.course_id === course.value) &&
    (channel.value === "all" || row.channel === channel.value) &&
    (!needle || `${row.subject} ${otherPeople(row, actor)}`.toLocaleLowerCase().includes(needle));
  const value = options.read.status === "ready" ? options.read.value : options.read.status === "stale" ? options.read.last : null;
  const read: Read<ConversationList> = options.read.status === "ready" && !options.read.value.conversations.length
    ? {status:"empty",sentence:words.noConversations,sub:words.startMessage}
    : options.read;
  const box: ListBox<ConversationList, CourseConversation> = {
    id: "conversations",
    kind: "list",
    job: "Find and reply to your own course conversations.",
    label: words.conversations,
    owner: {system:"imam"},
    read,
    readTime: true,
    fold: "never",
    state: () => ({class:"quiet",word:words.latestFirst}),
    detail: () => words.findConversation,
    draft: options.draft,
    figures: data => [{value:data.conversations.length,unit:data.conversations.length === 1 ? "conversation" : "conversations"}],
    rows: data => data.conversations.filter(shown),
    find: value ? {placeholder:words.search,facets:[channel,course],onQueryChange:options.onQueryChange,
      hidden:value.conversations.filter(row => !shown(row)).length} : undefined,
    row: {
      key: row => row.id,
      title: row => row.subject,
      state: row => options.task(row)?.result.state === "unknown"
        ? {class:"reading",word:words.replyUnknown}
        : row.unread ? {class:"reading",word:words.unread} : null,
      facts: row => [row.channel === "chat" ? words.chat : words.email,otherPeople(row,actor),row.course_title,
        messageTime(row.latest_message_at),...(!row.unread ? [words.read] : [])].filter(Boolean).join(" · "),
      act: () => ({does:"open",verb:words.open}),
      startsOpen: row => row.id === options.selected,
      detail: row => row.refresh_state === "unavailable"
        ? <p className="text-ui text-muted-foreground">{words.conversationUnavailable}</p>
        : <><ConversationThread conversation={row} actor={actor} />
          {options.mark?.(row.id).failed && <div role="status" className="grid gap-2"><p>{words.conversationReadFailed}</p>
            <Button variant="ghost" disabled={options.mark(row.id).pending} onClick={options.mark(row.id).retry}>{words.tryMarkRead}</Button></div>}</>,
      task: options.task,
    },
  };
  return box;
}
